import type { Point2d, Scale2d } from "@logic/lib/2d"
import { nanoid } from "nanoid"

import dice from "~/utils/dice"

export const getRandomPoint = (): Point2d => ({
  x: dice(window.innerHeight),
  y: dice(window.innerHeight),
})

export const nameNode = (): string => `${nanoid().slice(0, 8)}`

export type GeoPoint = [lat: number, lon: number]

const convertPxPointToGeo = (
  pxPoint: Point2d,
  geoPointCenter: GeoPoint,
  size: Scale2d,
  zoom = 21,
): GeoPoint => {
  const [centerLat, centerLon] = geoPointCenter
  const parallelMultiplier = Math.cos((centerLat * Math.PI) / 180)
  const degreesPerPixelX = 360 / 2 ** (zoom + 8)
  const degreesPerPixelY = (360 / 2 ** (zoom + 8)) * parallelMultiplier

  const lat = -1 * ((pxPoint.y - size.h / 2) * degreesPerPixelY - centerLat)
  const lon = (pxPoint.x - size.w / 2) * degreesPerPixelX + centerLon

  return [lat, lon]
}

const convertGeoPointToPx = (
  geoPoint: GeoPoint,
  geoPointCenter: GeoPoint,
  size: Scale2d,
  zoom = 21,
): Point2d => {
  const [lat, lon] = geoPoint
  const [centerLat, centerLon] = geoPointCenter

  const parallelMultiplier = Math.cos((centerLat * Math.PI) / 180)
  const degreesPerPixelX = 360 / 2 ** (zoom + 8)
  const degreesPerPixelY = (360 / 2 ** (zoom + 8)) * parallelMultiplier

  const y = (centerLat - lat) / degreesPerPixelY + size.h / 2
  const x = (lon - centerLon) / degreesPerPixelX + size.w / 2

  return { x, y }
}

const DEFAULT_SIDE = 1024

export const convertPxPolygonToGeo = (
  pxPoints: Point2d[],
  geoPointCenter: GeoPoint,
  size: Scale2d = { h: DEFAULT_SIDE, w: DEFAULT_SIDE },
): GeoPoint[] =>
  pxPoints.map((pxPoint) => convertPxPointToGeo(pxPoint, geoPointCenter, size))

export const convertGeoPointsToPx = (
  geoPoints: GeoPoint[],
  geoPointCenter: GeoPoint,
  size: Scale2d = { h: DEFAULT_SIDE, w: DEFAULT_SIDE },
): Point2d[] =>
  geoPoints.map((geoPoint) =>
    convertGeoPointToPx(geoPoint, geoPointCenter, size),
  )
